import AppButton from 'src/components/base/Button/Button';
import React, { useEffect, useState } from 'react';
import './InsuranceManagementTodo.scss';
import RadioCard from '../../../RadioCard/RadioCard';
import SearchableDropdown from '../../../SearchableDropdown/SearchableDropdown';
import NewInsuranceOption from '../NewInsuranceOption/NewInsuranceOption';
import { useDispatch, useSelector } from 'react-redux';
import { Typography } from 'src/latest/src/components';
import {
  clearModalContent,
  setModalContent,
} from 'src/redux/modules/modalEvent';
import { getInsurancesConfig } from 'src/redux/services/commonServices';
import { updateIfollowedUpTodo } from 'src/redux/services/loggingServices';
import { Logger } from 'src/latest/src/services';
import { setFlashNotification } from '../../../../../../redux/modules/flashNotification';
import { useIsMobile } from 'src/latest/src/hooks';
import {
  InsuranceAction,
  InsuranceRequestOption,
  StepType,
} from 'src/latest/src/constants/InsuranceManagement';
import { useTranslation } from 'react-i18next';

interface Props {
  todo: any;
  onVerified?: () => void;
}

interface Option {
  label: string;
  value: string;
}

const InsuranceManagementTodo: React.FC<Props> = ({ onVerified, todo }) => {
  const [selectedOption, setSelectedOption] = useState<InsuranceAction | ''>(
    '',
  );
  const [InsuranceList, setInsuranceList] = useState<any[] | null>(null);
  const [linkedInsuranceName, setLinkedInsuranceName] = useState<string>('');
  const [selectedInsurance, setSelectedInsurance] = useState<Option | null>(
    null,
  );
  const [step, setStep] = useState<StepType>(StepType.SELECT);
  const [loading, setLoading] = useState<boolean>(false);
  const [editedInsuranceTitle, setEditedInsuranceTitle] = useState(
    todo?.metadata?.insurance_title || '',
  );

  const { t } = useTranslation('components');
  const dispatch = useDispatch();
  const currentModal = useSelector((state: any) => state?.modalEvent);

  const getResponsePayload = () => {
    switch (selectedOption) {
      case InsuranceAction.LINK:
        return {
          action_metadata: {
            insurance_title: todo?.metadata?.insurance_title,
            linked_insurance_title: selectedInsurance?.label,
          },
          insurance_request_option: InsuranceRequestOption.LINK,
        };

      case InsuranceAction.ADD:
        return {
          action_metadata: {
            insurance_title: editedInsuranceTitle,
          },
          insurance_request_option: InsuranceRequestOption.ADD,
        };

      case InsuranceAction.REJECT:
        return {
          action_metadata: {
            insurance_title: todo?.metadata?.insurance_title,
          },
          insurance_request_option: InsuranceRequestOption.REJECT,
        };

      default:
        return null;
    }
  };

  const createFollowUp = async () => {
    const respObj = getResponsePayload();

    if (!respObj) return;

    try {
      setLoading(true);
      await updateIfollowedUpTodo(todo.id, respObj);
      onVerified?.();
      dispatch(clearModalContent());
    } catch (e) {
      dispatch(
        setFlashNotification({
          message: `Follow up error`,
          isError: true,
        }),
      );
      Logger.log('Follow up error', e);
    } finally {
      setLoading(false);
    }
  };

  const insuranceOptions =
    InsuranceList?.map(item => ({
      label: item.title,
      value: item.unique_key,
    })) || [];

  useEffect(() => {
    const headerText =
      step === StepType.CONFIRM
        ? t('insuranceManagementTodo.header.confirmation')
        : t('insuranceManagementTodo.header.newInsuranceRequest');

    if (!currentModal?.modalContent) return;

    dispatch(
      setModalContent({
        modalContent: currentModal.modalContent,
        title: (
          <Typography color="color-28" variant="size-20">
            {headerText}
          </Typography>
        ),
      }),
    );
  }, [step]);

  const getButtonLabel = () => {
    switch (selectedOption) {
      case InsuranceAction.LINK:
        return t('insuranceManagementTodo.buttons.link');
      case InsuranceAction.ADD:
        return t('insuranceManagementTodo.buttons.add');
      case InsuranceAction.REJECT:
        return t('insuranceManagementTodo.buttons.reject');
      default:
        return t('insuranceManagementTodo.buttons.proceedNext');
    }
  };

  const getInsuranceList = async () => {
    try {
      const resp = await getInsurancesConfig();
      const configString = resp?.config_values?.[0]?.config_value;

      let parsedData = null;

      try {
        parsedData = JSON.parse(configString);
      } catch (err) {
        dispatch(
          setFlashNotification({
            message: t('insuranceManagementTodo.errors.invalidJson'),
            isError: true,
          }),
        );
        Logger.log(t('insuranceManagementTodo.errors.invalidJson'), err);
      }

      setInsuranceList(parsedData?.data || []);
    } catch (e) {
      dispatch(
        setFlashNotification({
          message: t('insuranceManagementTodo.errors.fetchFailed'),
          isError: true,
        }),
      );
      Logger.log(t('insuranceManagementTodo.errors.fetchFailed'), e);
    }
  };

  useEffect(() => {
    if (selectedOption === InsuranceAction.LINK && !InsuranceList) {
      getInsuranceList();
    }
  }, [selectedOption]);

  useEffect(() => {
    if (selectedOption !== InsuranceAction.LINK) {
      setLinkedInsuranceName('');
      setSelectedInsurance(null);
    }
  }, [selectedOption]);

  useEffect(() => {
    if (selectedOption !== InsuranceAction.ADD) {
      setEditedInsuranceTitle(todo?.metadata?.insurance_title);
    }
  }, [selectedOption]);

  const isMobile = useIsMobile();

  return (
    <div
      className={
        isMobile && step === StepType.CONFIRM
          ? 'insurance-management-mobile-todo-container'
          : 'insurance-management-todo-container'
      }
    >
      {step === StepType.SELECT ? (
        <>
          <div className="text-start todo-header">
            <div>
              <span className="color-gray">
                {t('insuranceManagementTodo.header.insuranceLabel')}
              </span>
              <span className="color-black insurance-name">
                {todo?.metadata?.insurance_title}
              </span>
            </div>
            <div className="mt-2">
              <span className="color-gray">
                {t('insuranceManagementTodo.header.requestedByLabel')}
              </span>
              <span className="color-black provider-name">
                {todo?.metadata?.provider_name}
              </span>
            </div>
          </div>
          <div className="radio-cards-container d-flex flex-column align-items-center">
            <div className="mt-4">
              <RadioCard
                value={InsuranceAction.LINK}
                selectedValue={selectedOption}
                onChange={val => setSelectedOption(val as InsuranceAction | '')}
                title={t('insuranceManagementTodo.radio.linkTitle')}
                description={t('insuranceManagementTodo.radio.linkDesc')}
              >
                <SearchableDropdown
                  options={insuranceOptions}
                  value={linkedInsuranceName}
                  placeholder={t(
                    'insuranceManagementTodo.dropdown.placeholder',
                  )}
                  onInputChange={val => {
                    setLinkedInsuranceName(val);
                    setSelectedInsurance(null);
                  }}
                  onSelect={option => {
                    setLinkedInsuranceName(option.label);
                    setSelectedInsurance(option);
                  }}
                />
                {selectedInsurance && (
                  <div className="insurance-link-info mt-3 text-start">
                    {t('insuranceManagementTodo.info.linkInfo', {
                      selected: selectedInsurance?.label,
                      requested: todo?.metadata?.insurance_title,
                    })}
                  </div>
                )}
              </RadioCard>
            </div>
            <div className="mt-4">
              <RadioCard
                value={InsuranceAction.ADD}
                selectedValue={selectedOption}
                onChange={val => setSelectedOption(val as InsuranceAction | '')}
                title={t('insuranceManagementTodo.radio.addTitle')}
                description={
                  selectedOption === InsuranceAction.ADD
                    ? ''
                    : t('insuranceManagementTodo.radio.addDesc')
                }
              >
                <NewInsuranceOption
                  insuranceTitle={editedInsuranceTitle}
                  onChange={val => setEditedInsuranceTitle(val)}
                />
              </RadioCard>
            </div>
            <div className="mt-4">
              <RadioCard
                value={InsuranceAction.REJECT}
                selectedValue={selectedOption}
                onChange={val => setSelectedOption(val as InsuranceAction | '')}
                title={t('insuranceManagementTodo.radio.rejectTitle')}
                description={t('insuranceManagementTodo.radio.rejectDesc')}
              />
            </div>
          </div>

          <AppButton
            onClick={() => setStep(StepType.CONFIRM)}
            disabled={
              !selectedOption ||
              (selectedOption === InsuranceAction.LINK && !selectedInsurance)
            }
            label={getButtonLabel()}
            className="proceed-button"
            textWeight="semi-bold"
          />
        </>
      ) : (
        <div className="insurance-management-confirmation-view">
          {selectedOption === InsuranceAction.LINK ? (
            <>
              <div className="insurance-management-confirmation-view__linkContent">
                <div className="insurance-management-confirmation-view__question">
                  {t('insuranceManagementTodo.confirmation.linkQuestion', {
                    requested: todo?.metadata?.insurance_title,
                    linked:
                      linkedInsuranceName?.trim() ||
                      t(
                        'insuranceManagementTodo.confirmation.fallbackInsurance',
                      ),
                  })}
                </div>
              </div>
              <div className="insurance-management-confirmation-view__actions insurance-management-confirmation-view__actions-link">
                <AppButton
                  label={t('insuranceManagementTodo.buttons.yes')}
                  className="confirm-button"
                  textWeight="semi-bold"
                  isProcessing={loading}
                  onClick={() => {
                    createFollowUp();
                  }}
                />
                <AppButton
                  label={t('insuranceManagementTodo.buttons.no')}
                  onClick={() => setStep(StepType.SELECT)}
                  className="close-button bg-white"
                  textWeight="bold"
                  textSize="size-14"
                  variant="light"
                />
              </div>
              <div className="insurance-management-confirmation-view__note">
                {t('insuranceManagementTodo.confirmation.note', {
                  linked: linkedInsuranceName?.trim(),
                  andText: linkedInsuranceName?.trim()
                    ? t('insuranceManagementTodo.confirmation.andText', {
                        requested: todo?.metadata?.insurance_title,
                      })
                    : '',
                  provider: todo?.metadata?.provider_name,
                  finalInsurance:
                    linkedInsuranceName?.trim() ||
                    todo?.metadata?.provider_name,
                })}
              </div>
            </>
          ) : (
            <>
              <div className="insurance-management-confirmation-view__headerInfo">
                <div className="insurance-management-confirmation-view__headerRow">
                  <span className="insurance-management-confirmation-view__label label-insurance">
                    {t('insuranceManagementTodo.header.insuranceLabel')}
                  </span>
                  <span className="insurance-management-confirmation-view__value">
                    {selectedOption === InsuranceAction.ADD
                      ? editedInsuranceTitle
                      : todo?.metadata?.insurance_title}
                  </span>
                </div>
                <div className="insurance-management-confirmation-view__headerRow">
                  <span className="insurance-management-confirmation-view__label">
                    {t('insuranceManagementTodo.header.requestedByLabel')}
                  </span>
                  <span className="insurance-management-confirmation-view__value">
                    {todo?.metadata?.provider_name}
                  </span>
                </div>
              </div>
              <div className="insurance-management-confirmation-view__card">
                <div className="insurance-management-confirmation-view__cardTitle text-start">
                  {selectedOption === InsuranceAction.ADD
                    ? t('insuranceManagementTodo.confirmation.addTitle')
                    : t('insuranceManagementTodo.confirmation.rejectTitle')}
                </div>
                <div className="insurance-management-confirmation-view__cardDesc text-start">
                  {selectedOption === InsuranceAction.ADD
                    ? t('insuranceManagementTodo.confirmation.addDesc')
                    : t('insuranceManagementTodo.confirmation.rejectDesc')}
                </div>
              </div>
              <div className="insurance-management-confirmation-view__actions">
                <AppButton
                  label={t('insuranceManagementTodo.buttons.proceed')}
                  isProcessing={loading}
                  onClick={() => {
                    createFollowUp();
                  }}
                  className="confirm-button"
                  textWeight="bold"
                  textSize="size-14"
                />
                <AppButton
                  label={t('insuranceManagementTodo.buttons.cancel')}
                  onClick={() => setStep(StepType.SELECT)}
                  className="close-button bg-white"
                  textWeight="bold"
                  textSize="size-14"
                  variant="light"
                />
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default InsuranceManagementTodo;
